import { useMemo } from 'react';
import { usePlatformData } from './usePlatformData';
import type { ClassificationCatalogue } from '../lib/classificationCategoryResolver';

export type CategoryCatalogues = {
  /** For a PAGE's classification ranking — page categories only. */
  page: ClassificationCatalogue | undefined;
  /**
   * For a DOCUMENT's classification ranking. A document-level tuple can name a supertype
   * rather than a page category (see `DocumentClassification.ts` — the supertype rollup rows),
   * so both vocabularies count as live here.
   */
  document: ClassificationCatalogue | undefined;
};

/**
 * The live category catalogue in the shape `resolveClassificationRows` consumes (DR-3784).
 * Both scopes share `namesById`: a classification tuple's category id always points at a row
 * in the `categories` table, whichever level the ranking was computed at.
 *
 * Both are `undefined` until the categories query resolves, which the resolver reads as
 * "still loading" and passes rows through unchanged.
 */
export function useCategoryCatalogue(): CategoryCatalogues {
  const data = usePlatformData().categories.data;

  return useMemo(() => {
    if (!data) {
      return { page: undefined, document: undefined };
    }

    // Passed through as-is, NOT defaulted to `{}`: an older server sends no `pageCategoriesById`
    // at all, and an empty map would mark every id-bearing row dead instead of falling back to
    // name matching.
    const namesById = data.pageCategoriesById;

    return {
      page: { namesById, liveNames: new Set(data.pageCategories) },
      document: { namesById, liveNames: new Set([...data.pageCategories, ...data.documentCategories]) },
    };
  }, [data]);
}
